import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, ArrowLeft, Check, Star, Crown, Sparkles, FileText, Users, ArrowRight, Orbit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { createCheckout, getReportUsage } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { trackEvent } from "@/lib/useAnalytics";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReportDetailsForm } from "@/components/ReportDetailsForm";

const PREMIUM_FEATURES = [
  "Full Premium Lunar Climate reports (free for all)",
  "4 generated reports per month included",
  "Natal Report, Compatibility, Dasha Forecast",
  "Additional reports at $1.00 each",
  "Early access to new features",
];

const SINGLE_REPORTS = [
  {
    id: "natal_report",
    name: "Natal Report",
    description: "Complete AI-generated lunar birth analysis",
    price: "$1.99",
    route: "/calculator",
    icon: FileText,
    features: ["Lunar identity analysis", "Emotional patterns", "Growth path guidance"],
  },
  {
    id: "compatibility",
    name: "Compatibility Analysis",
    description: "Two-person nakshatra synastry",
    price: "$3.99",
    route: "/compatibility",
    icon: Users,
    features: ["Multi-factor scoring", "AI relationship insights", "Communication dynamics"],
  },
  {
    id: "dasha_forecast",
    name: "Dasha Forecast",
    description: "Vimshottari planetary period analysis",
    price: "$1.99",
    route: "/dasha",
    icon: Orbit,
    features: ["Current Mahadasha & Antardasha", "Timeline view", "Practical guidance"],
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState("");
  const [detailsFor, setDetailsFor] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isPremium = usage?.is_premium;

  useEffect(() => {
    if (user) {
      getReportUsage().then(setUsage).catch(console.error);
    }
  }, [user]);

  const handleSubscribe = async (planId) => {
    setLoading(planId);
    trackEvent("checkout_started", { plan: planId });
    try {
      const checkout = await createCheckout({
        package_id: planId,
        origin_url: window.location.origin,
      });
      if (checkout.url) window.location.href = checkout.url;
    } catch (e) {
      toast.error("Failed to start checkout");
    } finally {
      setLoading("");
    }
  };

  const handleBuyReport = (report) => {
    setDetailsFor(report);
  };

  const startCheckout = async (form) => {
    const report = detailsFor;
    if (!report) return;
    setLoading(report.id);
    setSubmitting(true);
    trackEvent("checkout_started", { report: report.id });
    try {
      const checkout = await createCheckout({
        package_id: report.id,
        origin_url: window.location.origin,
        metadata: form,
      });
      if (checkout.url) {
        window.location.href = checkout.url;
      } else {
        throw new Error("no url");
      }
    } catch (e) {
      toast.error("Failed to start checkout");
      setSubmitting(false);
      setLoading("");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors" data-testid="pricing-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Dashboard</span>
          </Link>
        </div>
      </nav>

      <main className="pt-14 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium tracking-wider uppercase">Premium Reports</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-light text-white/95 mb-4" data-testid="pricing-title">Pricing</h1>
            <p className="text-white/40 text-base mb-2 max-w-lg mx-auto">
              Premium Lunar Climate is now free for everyone. Subscribe for included reports or purchase individually.
            </p>
            
            {/* Premium user status */}
            {user && usage?.is_premium && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mt-4">
                <Crown className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm" data-testid="premium-status">
                  Premium Active — {usage.total_remaining ?? usage.remaining_included ?? 0} of {usage.limit} reports remaining this month
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Premium Subscription */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-12"
        >
          <div
            className="rounded-2xl p-8 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(255,255,255,0.03) 100%)",
              border: "1px solid rgba(251,191,36,0.2)",
            }}
            data-testid="premium-subscription-card"
          >
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs tracking-wider uppercase font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30">
              Best Value
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Crown className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-medium text-white/90">Premium</h2>
                    <p className="text-white/40 text-sm">Unlock all reports</p>
                  </div>
                </div>
                <ul className="space-y-2 mt-4">
                  {PREMIUM_FEATURES.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-amber-400/70 mt-0.5 flex-shrink-0" />
                      <span className="text-white/60">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col gap-3 min-w-[200px]">
                <Button
                  onClick={() => handleSubscribe("premium_monthly")}
                  disabled={loading === "premium_monthly" || (user && usage?.is_premium)}
                  className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 rounded-full h-12 text-sm"
                  data-testid="subscribe-monthly-btn"
                >
                  {loading === "premium_monthly" ? "Loading..." : user && usage?.is_premium ? "Already Subscribed" : "$4.29/month"}
                </Button>
                <Button
                  onClick={() => handleSubscribe("premium_yearly")}
                  disabled={loading === "premium_yearly" || (user && usage?.is_premium)}
                  variant="outline"
                  className="border-amber-500/20 text-amber-400/80 hover:bg-amber-500/10 rounded-full h-12 text-sm"
                  data-testid="subscribe-yearly-btn"
                >
                  {loading === "premium_yearly" ? "Loading..." : "$29/year (Save 44%)"}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Single Reports */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <h3 className="text-white/50 text-xs tracking-[0.2em] uppercase text-center mb-6">Or Purchase Individual Reports</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {SINGLE_REPORTS.map((report, i) => (
              <div
                key={report.id}
                className="rounded-xl p-6"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`report-card-${report.id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                    <report.icon className="w-5 h-5 text-white/50" />
                  </div>
                  <div>
                    <p className="text-white/90 font-medium">{report.name}</p>
                    <p className="text-white/40 text-xs">{report.price}</p>
                  </div>
                </div>
                <p className="text-white/50 text-sm mb-4">{report.description}</p>
                <ul className="space-y-1.5 mb-5">
                  {report.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-xs text-white/40">
                      <Check className="w-3 h-3 text-white/30" />
                      {f}
                    </li>
                  ))}
                </ul>
                {isPremium && (usage?.total_remaining ?? 0) > 0 ? (
                  <Button
                    onClick={() => navigate(report.route)}
                    className="w-full bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 rounded-full h-10 text-sm gap-2"
                    data-testid={`generate-${report.id}-btn`}
                  >
                    <Crown className="w-4 h-4" />
                    Generate — Included
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleBuyReport(report)}
                    disabled={loading === report.id}
                    variant="outline"
                    className="w-full border-white/10 text-white/60 hover:text-white hover:bg-white/5 rounded-full h-10 text-sm"
                    data-testid={`buy-${report.id}-btn`}
                  >
                    {loading === report.id ? "Loading..." : `Purchase ${report.price}`}
                  </Button>
                )}
              </div>
            ))}

            {/* Karmic Timeline — routes to dedicated page (premium-free vs paid handled there) */}
            <div
              className="rounded-xl p-6 flex flex-col"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(167,139,250,0.18)" }}
              data-testid="report-card-karmic"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                  <Orbit className="w-5 h-5 text-violet-300/80" />
                </div>
                <div>
                  <p className="text-white/90 font-medium">Karmic Timeline</p>
                  <p className="text-white/40 text-xs">{usage?.is_premium ? "Included in Premium" : "$1.99"}</p>
                </div>
              </div>
              <p className="text-white/50 text-sm mb-4">An 18-month Rahu–Ketu axis outlook — the themes your nodes bring into focus now, and what shifts next.</p>
              <ul className="space-y-1.5 mb-5">
                {["Current & upcoming nodal axis", "Houses & dates calculated", "18-month timeline"].map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-xs text-white/40">
                    <Check className="w-3 h-3 text-white/30" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate("/karmic")}
                className="w-full mt-auto bg-violet-500/15 text-violet-200 hover:bg-violet-500/25 border border-violet-400/25 rounded-full h-10 text-sm gap-2"
                data-testid="buy-karmic-btn"
              >
                <Orbit className="w-4 h-4" />
                {usage?.is_premium ? "Generate — Included" : "Open Report — $1.99"}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Free Lunar Climate Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <Star className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm">Premium Lunar Climate is free for everyone!</span>
          </div>
          <p className="text-white/30 text-xs mt-3">
            The full daily Lunar Climate report is now available to all users at no cost.
          </p>
        </motion.div>
      </main>

      {/* Collect birth details before redirecting to Stripe so the report is ready instantly */}
      <Dialog
        open={!!detailsFor}
        onOpenChange={(o) => {
          if (!o) { setDetailsFor(null); setSubmitting(false); setLoading(""); }
        }}
      >
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="pricing-details-dialog">
          <DialogHeader>
            <DialogTitle className="text-white/90 font-light text-lg">{detailsFor?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-white/40 text-xs -mt-1">
            Enter birth details now so your report is ready the moment payment completes.
          </p>
          {detailsFor && (
            <ReportDetailsForm
              packageId={detailsFor.id}
              label={detailsFor.name}
              onSubmit={startCheckout}
              submitting={submitting}
              submitLabel={submitting ? undefined : `Continue to Payment — ${detailsFor.price}`}
              submittingLabel="Redirecting to secure checkout…"
            />
          )}
          <p className="text-white/25 text-[11px] text-center">Secured by Stripe · Your details are saved with this purchase.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
